import { useState, useCallback, useEffect, useRef } from 'react';
import { loadMentions, dismissMention } from '@/store/mentions/actions';
import type { UserId } from '@/shared/types/user';
import { useApiWithAuth } from '@/hooks/api';
import { useMentionsStore } from '@/store/mentions';
import { useActiveMentions, useRecentMentions } from './computed';

export function useMentions(userId: UserId, limit?: number) {
  const { api, auth } = useApiWithAuth();
  const store = useMentionsStore();
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);

  const loadUserMentions = useCallback(async () => {
    if (loaded.current || loading) {
      return;
    }

    setLoading(true);
    loaded.current = true;

    try {
      const response = await loadMentions({ api, auth, userId, limit });
      store.dispatch({ type: 'setMentions', data: response });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, auth, userId, loading, limit, store]);

  useEffect(() => {
    if (userId) {
      loadUserMentions();
    }
  }, [userId, loadUserMentions]);

  const active = useActiveMentions(store);
  const recent = useRecentMentions(store);

  return {
    loading,
    mentions: store.state.list,
    active,
    recent,
    loadMentions: loadUserMentions,
  };
}

export function useDismissMention() {
  const { api, auth } = useApiWithAuth();
  const [dismissing, setDismissing] = useState(false);
  const { dispatch } = useMentionsStore();

  const handleDismiss = async (id: string) => {
    setDismissing(true);
    try {
      const result = await dismissMention({ api, auth, id });
      dispatch({ type: 'dismissMention', id });

      return result;
    } finally {
      setDismissing(false);
    }
  };

  return {
    dismissMention: handleDismiss,
    dismissing,
  };
}
